import { Button, Card, Loader, Stack, Text } from '@components/ui';
import { CancelJobDialog, JobKindNote, jobListTitle } from '@features/jobs';
import {
  JobActions,
  JobInfoPanel,
  JobLocationPanel,
} from '@features/jobs/job-detail';
import type { JobData } from '@features/jobs/jobData';
import {
  useAcceptJobMutation,
  useCancelJobMutation,
  useJobDetailQuery,
  useOnMyWayMutation,
} from '@lib/api/jobs';
import { queryKeys } from '@lib/api/queryKeys';
import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const SingleJobDetail = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const jobId = (location.state as { jobId?: string })?.jobId;
  const { data, isLoading } = useJobDetailQuery(jobId);
  const jobData = (data ?? {}) as JobData;
  const acceptJob = useAcceptJobMutation();
  const cancelJob = useCancelJobMutation();
  const onMyWay = useOnMyWayMutation();
  const [openAlert, setOpenAlert] = useState(false);
  const [checked, setChecked] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const actionPending = acceptJob.isPending || cancelJob.isPending;

  const handleJobsAcceptedRes = (message: string): void => {
    toast.success(message);
  };

  const handleCancelJobsConfirm = (): void => {
    if (!jobId) return;
    cancelJob.mutate(jobId, {
      onSuccess: (message) => {
        setCancelDialogOpen(false);
        toast.success(message);
      },
    });
  };

  const handleChange = (nextChecked: boolean): void => {
    setChecked(nextChecked);
    onMyWay.mutate(jobData?.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.jobDetail(jobId) });
      },
    });
  };

  const handleJobsAccepted = (): void => {
    if (!jobData?.id) return;
    acceptJob.mutate(jobData.id, {
      onSuccess: handleJobsAcceptedRes,
    });
  };

  const handleR8tingDetail = (): void => {
    setOpenAlert(true);
  };

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Loader />
      </Box>
    );
  }

  const showCancelJob =
    jobData?.status === 'Accepted' &&
    jobData?.ratingStartedAt == null &&
    (jobData?.onMyWay == null || jobData?.onMyWay === false);

  const headerTitle = jobListTitle(jobData, t('jobDetail.fallbackTitle'));

  const trimmedLink = jobData?.link?.trim();
  const linkUrl = trimmedLink
    ? /^https?:\/\//i.test(trimmedLink)
      ? trimmedLink
      : `https://${trimmedLink}`
    : null;

  const mapQuery =
    jobData?.latitude != null && jobData?.longitude != null
      ? `${jobData.latitude},${jobData.longitude}`
      : jobData?.address;

  const mapUrl = mapQuery
    ? `https://maps.google.com?q=${encodeURIComponent(mapQuery)}`
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        boxSizing: 'border-box',
        bgcolor: 'background.default',
        px: { xs: 2, md: 4 },
        py: 3,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack gap="xs" sx={{ flex: 1, minWidth: 0 }}>
            <Text as="h2" variant="title">
              {headerTitle}
            </Text>
            <Text as="span" variant="body" tone="muted">
              {jobData?.category?.name} – {jobData?.subCategory?.name}
            </Text>
            <JobKindNote job={jobData} />
          </Stack>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
            <Text as="span" variant="title" sx={{ display: 'block' }}>
              ${Number((jobData?.buyerCost ?? 0) / 2).toLocaleString('en-US')}
            </Text>
            <Text as="span" variant="caption" tone="muted">
              {t('jobDetail.jobValue')}
            </Text>
          </Box>
          {showCancelJob ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setCancelDialogOpen(true)}
              disabled={actionPending}
            >
              {t('jobDetail.cancelJob')}
            </Button>
          ) : null}
        </Card>
        <CancelJobDialog
          open={cancelDialogOpen}
          loading={cancelJob.isPending}
          onClose={() => setCancelDialogOpen(false)}
          onConfirm={handleCancelJobsConfirm}
        />

        <JobInfoPanel
          job={jobData}
          showOnMyWay={showCancelJob}
          onMyWay={checked}
          onMyWayChange={handleChange}
          linkUrl={linkUrl}
        />
        <JobLocationPanel job={jobData} mapUrl={mapUrl} />
        <Card sx={{ mb: 2 }}>
          <Text as="h2" variant="subtitle" sx={{ mb: 1.5 }}>
            {t('jobDetail.specialInstructions')}
          </Text>
          <Text variant="body" tone="muted">
            {jobData?.specialInstructions ?? t('jobDetail.none')}
          </Text>
        </Card>
        <JobActions
          job={jobData}
          openAlert={openAlert}
          loading={actionPending}
          onCloseAlert={() => setOpenAlert(false)}
          onBeginRating={handleR8tingDetail}
          onAccept={handleJobsAccepted}
        />
      </Box>
    </Box>
  );
};

export default SingleJobDetail;
